import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable, type DataTableColumn } from '@admin/components/DataTable';
import Pagination from '@admin/components/Pagination';
import {
  fetchAllQuestions,
  listQuestions,
  type ListQuestionsParams,
} from '../services/questions';
import { ApiError } from '@admin/services/api';
import { KNOWN_CATEGORIES } from './categories';
import QuestionCard from './QuestionCard';
import { questionLanguageVisibility } from './contentLanguages';
import { Difficulty, QuestionType } from '@games/word-clash/shared/types';
import type { QuestionListItem, QuestionsListResponse } from '@games/word-clash/shared/types';

type ViewMode = 'table' | 'card';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

type ActiveFilter = 'all' | 'active' | 'inactive';

/**
 * Admin questions list. Server-side filters (type / difficulty / isActive)
 * trigger a refetch. The search input still filters client-side, but when a
 * term is present the screen eagerly loads the ENTIRE filtered bank (paging
 * the endpoint via `fetchAllQuestions`) and filters + paginates over that,
 * so matches surface from page 1 regardless of which server page they'd
 * otherwise fall on. Without a term, the normal per-page server pagination
 * is used. The bank is small (a few hundred rows) so the eager load is cheap.
 */
type HasFilter = 'any' | 'yes' | 'no';
const HAS_FILTERS = {
  hasValidAnswersTr: 'any',
  hasSpecialAnswers: 'any',
  hasSpecialAnswersTr: 'any',
  hasPlausibleWrongAnswers: 'any',
  hasAliases: 'any',
  hasAliasesTr: 'any',
} as const;
type HasFilterKey = keyof typeof HAS_FILTERS;

function hasFilterToBool(v: HasFilter): boolean | '' {
  if (v === 'yes') return true;
  if (v === 'no') return false;
  return '';
}

function isQuestionType(v: string | null): v is QuestionType {
  return v !== null && (Object.values(QuestionType) as string[]).includes(v);
}

function isDifficulty(v: string | null): v is Difficulty {
  return v !== null && (Object.values(Difficulty) as string[]).includes(v);
}

// Maps each content-presence filter to the URL query key it persists under.
const HAS_FILTER_PARAM: Record<HasFilterKey, string> = {
  hasValidAnswersTr: 'hasTrAnswers',
  hasSpecialAnswers: 'hasSpecial',
  hasSpecialAnswersTr: 'hasSpecialTr',
  hasPlausibleWrongAnswers: 'hasWrong',
  hasAliases: 'hasAliases',
  hasAliasesTr: 'hasAliasesTr',
};

export default function QuestionsList() {
  const navigate = useNavigate();

  // Every filter lives in the URL query string so the view is shareable,
  // survives a reload, AND survives a round-trip to the detail screen and
  // back (browser Back restores the URL → filters reconstruct from it). The
  // search box is the one exception that also keeps a local copy — see below.
  const [searchParams, setSearchParams] = useSearchParams();

  // Mutate the query string through a single helper. `resetPage` clears the
  // page param so changing any server-side filter snaps back to page 1.
  // Uses the functional-updater form so the mutation always sees the freshest
  // params — two filter changes in the same render tick compose instead of the
  // second clobbering the first off a stale snapshot. It also keeps this
  // callback's identity stable (no `searchParams` dep), which stabilizes every
  // downstream setter and stops the debounce effect from re-arming on each URL
  // change.
  const updateParams = useCallback(
    (mutate: (p: URLSearchParams) => void, resetPage = true) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          mutate(params);
          if (resetPage) params.delete('page');
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // View mode (`?view=card`). Table is the default — anything other than
  // 'card' falls back to it. Changing the view must NOT reset the page.
  const view: ViewMode = searchParams.get('view') === 'card' ? 'card' : 'table';
  const setView = useCallback(
    (next: ViewMode) =>
      updateParams(
        (p) => (next === 'card' ? p.set('view', 'card') : p.delete('view')),
        false,
      ),
    [updateParams],
  );

  // Validate URL-sourced enums against their known values (a hand-crafted
  // `?type=bogus` falls back to "no filter") — same discipline as activeFilter.
  const typeFilter: QuestionType | '' = isQuestionType(searchParams.get('type'))
    ? (searchParams.get('type') as QuestionType)
    : '';
  const setTypeFilter = useCallback(
    (next: QuestionType | '') =>
      updateParams((p) => (next ? p.set('type', next) : p.delete('type'))),
    [updateParams],
  );

  const difficultyFilter: Difficulty | '' = isDifficulty(
    searchParams.get('difficulty'),
  )
    ? (searchParams.get('difficulty') as Difficulty)
    : '';
  const setDifficultyFilter = useCallback(
    (next: Difficulty | '') =>
      updateParams((p) =>
        next ? p.set('difficulty', next) : p.delete('difficulty'),
      ),
    [updateParams],
  );

  const activeParam = searchParams.get('active');
  const activeFilter: ActiveFilter =
    activeParam === 'all'
      ? 'all'
      : activeParam === 'inactive'
        ? 'inactive'
        : 'active';
  const setActiveFilter = useCallback(
    (next: ActiveFilter) =>
      updateParams((p) =>
        next === 'active' ? p.delete('active') : p.set('active', next),
      ),
    [updateParams],
  );

  // Key the memo on the raw `category` string, NOT on the whole `searchParams`
  // object. Otherwise an unrelated URL change (e.g. `page`) hands back a fresh
  // `[]` reference, which churns `fetchList`'s identity and forces a needless
  // refetch — visible in search mode as re-loading the whole bank on paging.
  const categoryParamRaw = searchParams.get('category') ?? '';
  const categoryFilter = useMemo<readonly string[]>(
    () => (categoryParamRaw ? categoryParamRaw.split(',').filter(Boolean) : []),
    [categoryParamRaw],
  );
  const toggleCategory = useCallback(
    (slug: string) =>
      updateParams((p) => {
        const current = (p.get('category') ?? '').split(',').filter(Boolean);
        const next = current.includes(slug)
          ? current.filter((s) => s !== slug)
          : [...current, slug];
        if (next.length > 0) p.set('category', next.join(','));
        else p.delete('category');
      }),
    [updateParams],
  );
  const clearCategories = useCallback(
    () => updateParams((p) => p.delete('category')),
    [updateParams],
  );

  // Same stability concern as categoryFilter: key the memo on a serialized
  // signature of just the has-filter params so it keeps a stable identity when
  // an unrelated param (page / view / q) changes.
  const hasFilterParamRaw = (Object.keys(HAS_FILTER_PARAM) as HasFilterKey[])
    .map((key) => `${key}=${searchParams.get(HAS_FILTER_PARAM[key]) ?? ''}`)
    .join('&');
  const hasFilters = useMemo<Record<HasFilterKey, HasFilter>>(() => {
    const out = { ...HAS_FILTERS } as Record<HasFilterKey, HasFilter>;
    hasFilterParamRaw.split('&').forEach((pair) => {
      const [key, v] = pair.split('=') as [HasFilterKey, string];
      out[key] = v === 'yes' || v === 'no' ? v : 'any';
    });
    return out;
  }, [hasFilterParamRaw]);
  const setHasFilter = useCallback(
    (key: HasFilterKey, v: HasFilter) =>
      updateParams((p) => {
        if (v === 'any') p.delete(HAS_FILTER_PARAM[key]);
        else p.set(HAS_FILTER_PARAM[key], v);
      }),
    [updateParams],
  );

  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const setPage = useCallback(
    (next: number) =>
      updateParams(
        (p) => (next > 1 ? p.set('page', String(next)) : p.delete('page')),
        false,
      ),
    [updateParams],
  );

  // The active search term is the URL `q` param; the local `searchRaw` mirrors
  // it for a responsive input and is debounced back into the URL. Seeding from
  // the URL on mount is what makes the term survive a back-navigation.
  const search = (searchParams.get('q') ?? '').trim().toLowerCase();
  const [searchRaw, setSearchRaw] = useState(() => searchParams.get('q') ?? '');

  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; data: QuestionsListResponse }
    | { status: 'error'; message: string }
  >({ status: 'loading' });

  // Debounce the search input into the URL. A search now spans the whole bank
  // and paginates over the match set, so a term change MUST snap back to page
  // 1 — otherwise a stale `?page=5` could point past the (smaller) filtered
  // result set and show an empty page.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = searchRaw.trim();
      updateParams((p) => {
        // No-op when the term is unchanged (e.g. the mount tick after a
        // back-navigation restored `?q=…&page=3`) so we don't clobber the
        // restored page. Only a real term change resets to page 1.
        if ((p.get('q') ?? '').trim() === next) return;
        if (next) p.set('q', next);
        else p.delete('q');
        p.delete('page');
      }, false);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchRaw, updateParams]);

  // Refetch whenever server-side filters or page change. When a search term is
  // active we eagerly load the ENTIRE filtered bank (all pages) so the text
  // filter below can match across every page; otherwise we fetch just the
  // requested page.
  // In search mode the eager whole-bank load is page-independent (the client
  // slices it locally), so page navigation must NOT re-trigger the fetch —
  // otherwise every Next/Prev re-downloads the whole bank. Collapsing `page`
  // to a constant while searching keeps it out of the fetch dependency.
  const fetchPage = search ? 1 : page;
  const reqCounterRef = useRef(0);
  const fetchList = useCallback(async () => {
    const reqId = ++reqCounterRef.current;
    setState({ status: 'loading' });
    try {
      const baseParams: Omit<ListQuestionsParams, 'page' | 'pageSize'> = {
        type: typeFilter,
        difficulty: difficultyFilter,
        isActive:
          activeFilter === 'all'
            ? ''
            : activeFilter === 'active',
        category: categoryFilter.length > 0 ? categoryFilter : undefined,
        hasValidAnswersTr: hasFilterToBool(hasFilters.hasValidAnswersTr),
        hasSpecialAnswers: hasFilterToBool(hasFilters.hasSpecialAnswers),
        hasSpecialAnswersTr: hasFilterToBool(hasFilters.hasSpecialAnswersTr),
        hasPlausibleWrongAnswers: hasFilterToBool(
          hasFilters.hasPlausibleWrongAnswers,
        ),
        hasAliases: hasFilterToBool(hasFilters.hasAliases),
        hasAliasesTr: hasFilterToBool(hasFilters.hasAliasesTr),
      };
      let data: QuestionsListResponse;
      if (search) {
        // Search mode: pull the whole filtered bank and let the client filter
        // + paginate it. `total` is the un-searched bank size; the count line
        // reports the match count separately.
        const all = await fetchAllQuestions(baseParams);
        data = {
          data: all,
          page: 1,
          pageSize: PAGE_SIZE,
          total: all.length,
          hasMore: false,
        };
      } else {
        data = await listQuestions({
          ...baseParams,
          page: fetchPage,
          pageSize: PAGE_SIZE,
        });
      }
      // Ignore stale responses if a newer fetch has started.
      if (reqId !== reqCounterRef.current) return;
      setState({ status: 'ready', data });
    } catch (err) {
      if (reqId !== reqCounterRef.current) return;
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load questions';
      setState({ status: 'error', message });
    }
  }, [
    fetchPage,
    search,
    typeFilter,
    difficultyFilter,
    activeFilter,
    categoryFilter,
    hasFilters,
  ]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  // All rows matching the text search (across the whole bank in search mode).
  const filteredRows = useMemo<QuestionListItem[]>(() => {
    if (state.status !== 'ready') return [];
    if (!search) return state.data.data;
    return state.data.data.filter((q) => {
      const haystack = `${q.promptEn} ${q.promptTr}`.toLowerCase();
      return haystack.includes(search);
    });
  }, [state, search]);

  // Rows shown on the current page. Without a search the server already
  // paginated; with a search we hold the full match set in memory and slice
  // it client-side.
  const rows = useMemo<QuestionListItem[]>(() => {
    if (!search) return filteredRows;
    const start = (page - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, search, page]);

  // Navigate to the detail screen, carrying the current filter query so the
  // detail's in-page "← Back to Questions" link returns to the filtered list.
  // (The browser Back button restores it independently via the URL.)
  const openQuestion = useCallback(
    (id: string) =>
      navigate(`/questions/${id}`, {
        state: { listSearch: searchParams.toString() },
      }),
    [navigate, searchParams],
  );

  const totalPages = useMemo(() => {
    if (state.status !== 'ready') return 1;
    // Search mode paginates over the filtered match set, not the server total.
    if (search) {
      return Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
    }
    if (state.data.total === 0) return 1;
    return Math.max(1, Math.ceil(state.data.total / state.data.pageSize));
  }, [state, search, filteredRows]);

  const columns: DataTableColumn<QuestionListItem>[] = useMemo(
    () => [
      {
        key: 'promptEn',
        header: 'Prompt (EN)',
        render: (q) => {
          const { showEnglish } = questionLanguageVisibility(q.contentFlags);
          return showEnglish ? (
            <span className="font-medium text-slate-900">{q.promptEn}</span>
          ) : (
            <span className="text-slate-400">—</span>
          );
        },
      },
      {
        key: 'promptTr',
        header: 'Prompt (TR)',
        // Gated for the same reason as the card view. Without this the table
        // and card modes of THIS page disagreed about the same en-only row —
        // exactly the drift `contentLanguages.ts` exists to prevent.
        render: (q) => {
          const { showTurkish } = questionLanguageVisibility(q.contentFlags);
          return showTurkish ? (
            <span className="text-slate-700">{q.promptTr}</span>
          ) : (
            <span className="text-slate-400">—</span>
          );
        },
      },
      {
        key: 'type',
        header: 'Type',
        render: (q) => <TypePill type={q.type} />,
      },
      {
        key: 'difficulty',
        header: 'Difficulty',
        render: (q) => <DifficultyPill difficulty={q.difficulty} />,
      },
      {
        key: 'category',
        header: 'Category',
        render: (q) => (
          <span className="text-xs text-slate-500 font-mono">
            {q.category}
          </span>
        ),
      },
      {
        key: 'isActive',
        header: 'Active',
        render: (q) => (
          <span
            className={
              'inline-block text-xs px-2 py-0.5 rounded uppercase tracking-wide ' +
              (q.isActive
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-slate-200 text-slate-600')
            }
            data-testid={`row-active-${q.id}`}
          >
            {q.isActive ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        key: 'answersCount',
        header: 'Answers',
        render: (q) => (
          <div className="flex flex-col text-xs leading-tight">
            <span className="text-slate-700">EN: {q.validAnswers.length}</span>
            <span className="text-slate-500">
              TR: {q.validAnswersTr.length}
            </span>
          </div>
        ),
      },
      {
        key: 'extras',
        header: 'Aliases · Special · Wrong',
        render: (q) => (
          <div className="flex flex-col text-xs font-mono text-slate-600">
            <span>
              Aliases: {Object.keys(q.answerAliases).length}/
              {Object.keys(q.answerAliasesTr).length}
            </span>
            <span>
              Special: {q.specialAnswers.length}/
              {q.specialAnswersTr.length}
            </span>
            <span>Wrong: {q.plausibleWrongAnswers.length}</span>
          </div>
        ),
      },
      {
        key: 'createdAt',
        header: 'Created',
        render: (q) => (
          <span className="text-xs text-slate-500">
            {formatDate(q.createdAt)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4" data-testid="questions-list-screen">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-slate-900">Questions</h1>
        <div className="flex items-center gap-2">
          {/* Table / Card view toggle. Table is the default. */}
          <div
            className="inline-flex rounded border border-slate-300 overflow-hidden"
            role="group"
            aria-label="View mode"
            data-testid="questions-view-toggle"
          >
            <button
              type="button"
              onClick={() => setView('table')}
              aria-pressed={view === 'table'}
              data-testid="questions-view-table"
              className={
                'px-3 py-1.5 text-sm transition-colors ' +
                (view === 'table'
                  ? 'bg-slate-800 text-white'
                  : 'bg-white text-slate-700 hover:bg-slate-50')
              }
            >
              Table
            </button>
            <button
              type="button"
              onClick={() => setView('card')}
              aria-pressed={view === 'card'}
              data-testid="questions-view-card"
              className={
                'px-3 py-1.5 text-sm transition-colors ' +
                (view === 'card'
                  ? 'bg-slate-800 text-white'
                  : 'bg-white text-slate-700 hover:bg-slate-50')
              }
            >
              Card
            </button>
          </div>
          <button
            type="button"
            onClick={() => navigate('/questions/new')}
            data-testid="questions-create-button"
            className="text-sm rounded bg-slate-800 text-white px-3 py-1.5 hover:bg-slate-700"
          >
            + Create
          </button>
        </div>
      </div>

      {/* Filter row: stacks vertically below sm (each control full-width),
          wraps inline above sm. The pre-sweep version used `flex-wrap` only,
          which let the selects stay content-width on narrow viewports and
          collapse the label text into single characters (the symptom from
          the original mobile audit screenshot). */}
      <div className="bg-white rounded border border-slate-200 p-3 grid grid-cols-1 sm:flex sm:flex-wrap sm:items-end gap-3">
        <label className="flex flex-col text-xs text-slate-600 sm:flex-1 sm:min-w-[200px]">
          <span>Search prompts</span>
          <input
            type="text"
            value={searchRaw}
            onChange={(e) => setSearchRaw(e.target.value)}
            placeholder="Search all questions by prompt text…"
            data-testid="questions-search-input"
            className="mt-1 rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
          />
        </label>

        <label className="flex flex-col text-xs text-slate-600">
          Type
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as QuestionType | '')}
            data-testid="questions-filter-type"
            className="mt-1 w-full sm:w-auto rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            <option value="">All types</option>
            <option value={QuestionType.Category}>Category</option>
            <option value={QuestionType.Prefix}>Prefix</option>
          </select>
        </label>

        <label className="flex flex-col text-xs text-slate-600">
          Difficulty
          <select
            value={difficultyFilter}
            onChange={(e) =>
              setDifficultyFilter(e.target.value as Difficulty | '')
            }
            data-testid="questions-filter-difficulty"
            className="mt-1 w-full sm:w-auto rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            <option value="">All difficulties</option>
            <option value={Difficulty.Easy}>Easy</option>
            <option value={Difficulty.Medium}>Medium</option>
            <option value={Difficulty.Hard}>Hard</option>
          </select>
        </label>

        <label className="flex flex-col text-xs text-slate-600">
          Status
          <select
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value as ActiveFilter)}
            data-testid="questions-filter-active"
            className="mt-1 w-full sm:w-auto rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            <option value="all">All</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </select>
        </label>

      </div>

      {/* Category multi-select — a chip row of KNOWN_CATEGORIES that admins
          toggle on/off. Selecting none = no category filter. The URL keeps
          selections as a comma-separated slug list so views stay shareable. */}
      <div className="bg-white rounded border border-slate-200 p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Category
          </div>
          {categoryFilter.length > 0 && (
            <button
              type="button"
              onClick={clearCategories}
              data-testid="questions-filter-category-clear"
              className="text-xs rounded px-2 py-0.5 border border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              Clear ({categoryFilter.length})
            </button>
          )}
        </div>
        <div
          className="flex flex-wrap gap-2"
          data-testid="questions-filter-category"
          role="group"
          aria-label="Filter by category"
        >
          {KNOWN_CATEGORIES.map((c) => {
            const active = categoryFilter.includes(c.slug);
            return (
              <button
                type="button"
                key={c.slug}
                onClick={() => toggleCategory(c.slug)}
                aria-pressed={active}
                data-testid={`questions-filter-category-${c.slug}`}
                className={
                  'text-xs rounded-full px-2.5 py-1 border transition-colors ' +
                  (active
                    ? 'bg-slate-800 border-slate-800 text-white'
                    : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50')
                }
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content-presence filters — answers the "which questions have TR
          answers / aliases / special answers?" question admins keep asking. */}
      <div className="bg-white rounded border border-slate-200 p-3 space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Content filters
        </div>
        <div className="flex flex-wrap gap-3">
          <HasFilterControl
            label="TR answers"
            value={hasFilters.hasValidAnswersTr}
            onChange={(v) => setHasFilter('hasValidAnswersTr', v)}
            testId="questions-filter-has-tr-answers"
          />
          <HasFilterControl
            label="Aliases (EN)"
            value={hasFilters.hasAliases}
            onChange={(v) => setHasFilter('hasAliases', v)}
            testId="questions-filter-has-aliases"
          />
          <HasFilterControl
            label="Aliases (TR)"
            value={hasFilters.hasAliasesTr}
            onChange={(v) => setHasFilter('hasAliasesTr', v)}
            testId="questions-filter-has-aliases-tr"
          />
          <HasFilterControl
            label="Special answers (EN)"
            value={hasFilters.hasSpecialAnswers}
            onChange={(v) => setHasFilter('hasSpecialAnswers', v)}
            testId="questions-filter-has-special"
          />
          <HasFilterControl
            label="Special answers (TR)"
            value={hasFilters.hasSpecialAnswersTr}
            onChange={(v) => setHasFilter('hasSpecialAnswersTr', v)}
            testId="questions-filter-has-special-tr"
          />
          <HasFilterControl
            label="Plausible wrong"
            value={hasFilters.hasPlausibleWrongAnswers}
            onChange={(v) => setHasFilter('hasPlausibleWrongAnswers', v)}
            testId="questions-filter-has-wrong"
          />
        </div>
      </div>

      {state.status === 'error' && (
        <div
          role="alert"
          data-testid="questions-list-error"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
        >
          {state.message}
        </div>
      )}

      {/* Result count — reflects the server-side filtered total. When the
          search box is active it matches across the whole bank, so the count
          shows how many questions match alongside the unfiltered total. */}
      {state.status === 'ready' && (
        <div
          className="text-xs text-slate-600"
          data-testid="questions-count"
          aria-live="polite"
        >
          {search ? (
            <>
              <span className="font-semibold text-slate-800">
                {filteredRows.length.toLocaleString()}
              </span>{' '}
              of{' '}
              <span className="font-semibold text-slate-800">
                {state.data.total.toLocaleString()}
              </span>{' '}
              match “{search}”
            </>
          ) : (
            <>
              <span className="font-semibold text-slate-800">
                {state.data.total.toLocaleString()}
              </span>{' '}
              {state.data.total === 1 ? 'question' : 'questions'}
            </>
          )}
        </div>
      )}

      {view === 'table' ? (
        <DataTable<QuestionListItem>
          columns={columns}
          rows={rows}
          rowKey={(q) => q.id}
          loading={state.status === 'loading'}
          onRowClick={(q) => openQuestion(q.id)}
          empty={
            search
              ? 'No questions match the search text.'
              : 'No questions yet.'
          }
          testId="questions-table"
        />
      ) : (
        <QuestionCardGrid
          rows={rows}
          loading={state.status === 'loading'}
          empty={
            search
              ? 'No questions match the search text.'
              : 'No questions yet.'
          }
          onOpen={(q) => openQuestion(q.id)}
        />
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        disabled={state.status === 'loading'}
      />
    </div>
  );
}

/**
 * Three-state filter control: `any` (no filter), `yes` (has entries), `no`
 * (missing/empty). Renders as three segmented chips so the active state is
 * visible without opening a dropdown.
 */
function HasFilterControl({
  label,
  value,
  onChange,
  testId,
}: {
  label: string;
  value: HasFilter;
  onChange: (v: HasFilter) => void;
  testId: string;
}) {
  const options: Array<{ v: HasFilter; label: string }> = [
    { v: 'any', label: 'Any' },
    { v: 'yes', label: 'Yes' },
    { v: 'no', label: 'No' },
  ];
  return (
    <div className="flex flex-col text-xs text-slate-600" data-testid={testId}>
      <span>{label}</span>
      <div className="mt-1 inline-flex rounded border border-slate-300 overflow-hidden">
        {options.map((opt) => (
          <button
            type="button"
            key={opt.v}
            onClick={() => onChange(opt.v)}
            data-testid={`${testId}-${opt.v}`}
            aria-pressed={value === opt.v}
            className={
              'px-2 py-1 text-xs transition-colors ' +
              (value === opt.v
                ? 'bg-slate-800 text-white'
                : 'bg-white text-slate-700 hover:bg-slate-50')
            }
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Card-view body. Mirrors the DataTable's loading / empty / data states so the
 * two views are interchangeable, but lays the rows out as a responsive grid of
 * `QuestionCard`s.
 */
function QuestionCardGrid({
  rows,
  loading,
  empty,
  onOpen,
}: {
  rows: QuestionListItem[];
  loading: boolean;
  empty: string;
  onOpen: (q: QuestionListItem) => void;
}) {
  if (loading) {
    return (
      <div
        className="text-sm text-slate-500 bg-white rounded border border-slate-200 px-3 py-6 text-center"
        data-testid="questions-cards-loading"
      >
        Loading…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div
        className="text-sm text-slate-500 bg-white rounded border border-slate-200 px-3 py-6 text-center"
        data-testid="questions-cards-empty"
      >
        {empty}
      </div>
    );
  }
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3"
      data-testid="questions-cards"
    >
      {rows.map((q) => (
        <QuestionCard key={q.id} question={q} onOpen={onOpen} />
      ))}
    </div>
  );
}

function TypePill({ type }: { type: QuestionType }) {
  return (
    <span className="inline-block text-xs px-2 py-0.5 rounded uppercase tracking-wide bg-slate-100 text-slate-700">
      {type}
    </span>
  );
}

function DifficultyPill({ difficulty }: { difficulty: Difficulty }) {
  const palette =
    difficulty === Difficulty.Easy
      ? 'bg-emerald-100 text-emerald-800'
      : difficulty === Difficulty.Medium
        ? 'bg-amber-100 text-amber-800'
        : 'bg-red-100 text-red-800';
  return (
    <span
      className={`inline-block text-xs px-2 py-0.5 rounded uppercase tracking-wide ${palette}`}
    >
      {difficulty}
    </span>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}
